/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { ICliente, ICoordenadas, ISirena } from 'modelos/src';
import {
  MapboxService,
  TipoMapaBase,
} from '../../../auxiliares/mapbox/mapbox.service';
import {
  coordenadasToGeoJSONPolygon,
  latLngToLngLat,
} from '../../../auxiliares/mapbox/mapbox-coords';
import { sirenaFeature } from '../../../auxiliares/mapbox/sirena-feature';

/**
 * Mapa principal del panel: sirenas (con clustering y colores por estado),
 * zonas de clientes (polígonos) y emisión de sirena seleccionada. Reemplaza el
 * `<google-map>` + `deprecated-map-marker-clusterer` de Google Maps.
 */
@Component({
  selector: 'app-mapa-principal',
  standalone: true,
  template: `
    <div #cont class="mapa-principal-cont"></div>
    <button
      type="button"
      class="mapa-principal-toggle"
      (click)="toggleEstilo()"
      title="Cambiar tipo de mapa"
    >
      {{ estilo === 'calle' ? 'Satélite' : 'Calle' }}
    </button>
  `,
  styles: [
    `
      :host {
        display: block;
        position: relative;
        width: 100%;
        height: 100%;
      }
      .mapa-principal-cont {
        width: 100%;
        height: 100%;
      }
      .mapa-principal-toggle {
        position: absolute;
        bottom: 12px;
        right: 12px;
        z-index: 1;
        border: none;
        border-radius: 8px;
        padding: 8px 14px;
        background: rgba(255, 255, 255, 0.95);
        color: #0c4767;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(15, 37, 53, 0.25);
      }
    `,
  ],
})
export class MapaPrincipalComponent implements AfterViewInit, OnDestroy {
  private mapboxService = inject(MapboxService);
  private ngZone = inject(NgZone);

  private readonly cont =
    viewChild.required<ElementRef<HTMLDivElement>>('cont');

  readonly sirenas = input<ISirena[]>([]);
  readonly clientes = input<ICliente[]>([]);
  readonly mostrarSirenas = input<boolean>(true);
  readonly mostrarZonas = input<boolean>(true);
  readonly center = input<ICoordenadas | undefined>(undefined);
  readonly customPosition = input<ICoordenadas | undefined>(undefined);
  readonly verActualizaciones = input<boolean>(false);
  /** Filtro client-side: ids de cliente a mostrar (vacío = todas). */
  readonly filtroClientes = input<string[]>([]);
  /** Filtro client-side por estado online (undefined = ambos). */
  readonly filtroOnline = input<boolean | undefined>(undefined);
  readonly sirenaClick = output<ISirena>();

  public estilo: TipoMapaBase = 'calle';

  private map?: mapboxgl.Map;
  private cargado = false;
  private customMarker?: mapboxgl.Marker;
  private resizeObserver?: ResizeObserver;
  private rafId?: number;

  private readonly SRC_SIRENAS = 'sirenas';
  private readonly SRC_ZONAS = 'zonas';

  constructor() {
    // Re-render reactivo de datos + filtros cuando el mapa ya cargó.
    effect(() => {
      // dependencias reactivas
      this.sirenas(); this.mostrarSirenas(); this.filtroClientes(); this.filtroOnline(); this.verActualizaciones();
      const verZonas = this.mostrarZonas();
      const clientes = this.clientes();
      if (!this.cargado) return;
      this.renderSirenas();
      this.renderZonas(verZonas ? clientes : []);
    });
    // Centrado por geocodificación.
    effect(() => {
      const c = this.center();
      if (this.cargado && c) {
        this.map?.flyTo({ center: latLngToLngLat(c), zoom: 15 });
      }
    });
    // Marcador personalizado (dirección buscada).
    effect(() => {
      const c = this.customPosition();
      if (this.cargado) {
        this.renderCustomMarker(c);
      }
    });
  }

  ngAfterViewInit(): void {
    this.map = this.mapboxService.crearMapa(this.cont().nativeElement, {
      center: this.center(),
      zoom: 10,
      estilo: this.estilo,
    });
    this.map.on('load', () => {
      this.agregarCapas();
      this.cargado = true;
      this.renderSirenas();
      this.renderZonas(this.mostrarZonas() ? this.clientes() : []);
      this.renderCustomMarker(this.customPosition());
    });
    this.map.on('style.load', () => {
      if (this.cargado) {
        if (this.rafId != null) { cancelAnimationFrame(this.rafId); this.rafId = undefined; }
        this.agregarCapas();
        this.renderSirenas();
        this.renderZonas(this.mostrarZonas() ? this.clientes() : []);
      }
    });
    // Mapbox mide el contenedor al crearse; si arranca en 0px queda en
    // blanco. Re-dimensionar ante cualquier cambio de tamaño del contenedor.
    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.cont().nativeElement);
  }

  toggleEstilo(): void {
    this.estilo = this.estilo === 'calle' ? 'satelite' : 'calle';
    this.map?.setStyle(this.mapboxService.estiloUrl(this.estilo));
  }

  // ===================== Setup de capas =====================

  private agregarCapas(): void {
    if (!this.map) {
      return;
    }
    // Zonas
    if (!this.map.getSource(this.SRC_ZONAS)) {
      this.map.addSource(this.SRC_ZONAS, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      this.map.addLayer({
        id: `${this.SRC_ZONAS}-fill`,
        type: 'fill',
        source: this.SRC_ZONAS,
        paint: { 'fill-color': '#d32f2f', 'fill-opacity': 0.1 },
      });
      this.map.addLayer({
        id: `${this.SRC_ZONAS}-line`,
        type: 'line',
        source: this.SRC_ZONAS,
        paint: {
          'line-color': '#d32f2f',
          'line-width': 2,
          'line-opacity': 0.5,
        },
      });
    }
    // Sirenas
    this.agregarCapasSirenas();
  }

  private agregarCapasSirenas(): void {
    if (!this.map || this.map.getSource(this.SRC_SIRENAS)) return;
    this.map.addSource(this.SRC_SIRENAS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 12,
      clusterRadius: 45,
    });
    const COLORES = [
      'match', ['get', 'color'],
      'azul', '#1e88e5', 'verde', '#2e7d32', 'amarilla', '#f9a825', 'roja', '#e53935',
      '#e53935',
    ] as unknown as mapboxgl.ExpressionSpecification;
    this.map.addLayer({
      id: `${this.SRC_SIRENAS}-cluster`, type: 'circle', source: this.SRC_SIRENAS,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#0c4767', 'circle-opacity': 0.85,
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26],
        'circle-stroke-width': 2, 'circle-stroke-color': '#fff',
      },
    });
    this.map.addLayer({
      id: `${this.SRC_SIRENAS}-cluster-count`, type: 'symbol', source: this.SRC_SIRENAS,
      filter: ['has', 'point_count'],
      layout: { 'text-field': '{point_count_abbreviated}', 'text-size': 13 },
      paint: { 'text-color': '#fff' },
    });
    this.map.addLayer({
      id: `${this.SRC_SIRENAS}-pulso`, type: 'circle', source: this.SRC_SIRENAS,
      filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'activa'], true]],
      paint: { 'circle-color': COLORES, 'circle-opacity': 0.35, 'circle-radius': 8 },
    });
    this.map.addLayer({
      id: `${this.SRC_SIRENAS}-punto`, type: 'circle', source: this.SRC_SIRENAS,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': COLORES, 'circle-radius': 7,
        'circle-stroke-width': 2, 'circle-stroke-color': '#fff',
      },
    });
    this.registrarEventos();
  }

  private registrarEventos(): void {
    if (!this.map) {
      return;
    }
    // Click en cluster → zoom
    this.map.on('click', `${this.SRC_SIRENAS}-cluster`, (e) => {
      const features = this.map!.queryRenderedFeatures(e.point, {
        layers: [`${this.SRC_SIRENAS}-cluster`],
      });
      const clusterId = features[0]?.properties?.['cluster_id'];
      const source = this.map!.getSource(
        this.SRC_SIRENAS,
      ) as mapboxgl.GeoJSONSource;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }
        const geom = features[0].geometry as GeoJSON.Point;
        this.map!.easeTo({
          center: geom.coordinates as [number, number],
          zoom: zoom ?? 14,
        });
      });
    });
    // Click en sirena → emitir
    this.map.on('click', `${this.SRC_SIRENAS}-punto`, (e) => {
      const id = e.features?.[0]?.properties?.['id'];
      if (!id) return;
      const sirena = (this.sirenas() || []).find((s) => s._id === id);
      if (sirena) this.ngZone.run(() => this.sirenaClick.emit(sirena));
    });
    // Cursor
    ['cluster', 'punto'].forEach((capa) => {
      const id = `${this.SRC_SIRENAS}-${capa}`;
      this.map!.on('mouseenter', id, () => {
        this.map!.getCanvas().style.cursor = 'pointer';
      });
      this.map!.on('mouseleave', id, () => {
        this.map!.getCanvas().style.cursor = '';
      });
    });
  }

  // ===================== Render de datos =====================

  private sirenasVisibles(): ISirena[] {
    if (!this.mostrarSirenas()) return [];
    const ids = this.filtroClientes();
    const online = this.filtroOnline();
    return (this.sirenas() || []).filter((s) => {
      if (ids.length && !ids.includes((s as { idCliente?: string }).idCliente ?? '')) return false;
      if (online !== undefined && !!s.online !== online) return false;
      return true;
    });
  }

  private renderSirenas(): void {
    const source = this.map?.getSource(this.SRC_SIRENAS) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) return;
    const ver = this.verActualizaciones();
    const features = this.sirenasVisibles()
      .map((s) => sirenaFeature(s, ver))
      .filter((f): f is GeoJSON.Feature<GeoJSON.Point> => !!f);
    source.setData({ type: 'FeatureCollection', features });
    this.actualizarPulso();
  }

  private renderZonas(clientes: ICliente[]): void {
    const source = this.map?.getSource(this.SRC_ZONAS) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) {
      return;
    }
    const features: GeoJSON.Feature[] = [];
    (clientes || []).forEach((c) => {
      (c.coordenadas || []).forEach((anillo) => {
        if (anillo?.length) {
          features.push(coordenadasToGeoJSONPolygon([anillo]));
        }
      });
    });
    source.setData({ type: 'FeatureCollection', features });
  }

  private renderCustomMarker(coord?: ICoordenadas): void {
    if (!this.map) {
      return;
    }
    if (!coord) {
      this.customMarker?.remove();
      this.customMarker = undefined;
      return;
    }
    const lngLat = latLngToLngLat(coord);
    if (this.customMarker) {
      this.customMarker.setLngLat(lngLat);
    } else {
      this.customMarker = new mapboxgl.Marker({ color: '#0c4767' })
        .setLngLat(lngLat)
        .addTo(this.map);
    }
  }

  // ===================== Pulso animado =====================

  private actualizarPulso(): void {
    const hayActivas = this.sirenasVisibles().some(
      (s) => !!s.online && (!!s.sonidoEncendido || !!s.luzEncendida),
    );
    if (hayActivas && this.rafId == null && this.cargado) {
      const animar = (t: number) => {
        const fase = (t % 1600) / 1600;
        this.map?.setPaintProperty(`${this.SRC_SIRENAS}-pulso`, 'circle-radius', 8 + fase * 22);
        this.map?.setPaintProperty(`${this.SRC_SIRENAS}-pulso`, 'circle-opacity', 0.4 * (1 - fase));
        this.rafId = requestAnimationFrame(animar);
      };
      this.rafId = requestAnimationFrame(animar);
    } else if (!hayActivas && this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.customMarker?.remove();
    this.map?.remove();
  }
}
